import { useState } from 'react';
import { Check, Pencil, Trash2, X } from 'lucide-react';
import { fetchApi } from '../../api';
import type { Pipeline, PipelineStageItem } from './types';

interface PipelineManagerModalProps {
  pipelines: Pipeline[];
  selectedPipelineId: number | null;
  onSelectPipeline: (pipelineId: number) => void;
  onPipelinesChanged: () => void;
  onClose: () => void;
  onError: (message: string) => void;
  canManage: boolean;
}

const BUSINESS_TYPES = [
  { id: 'RETAIL', label: 'Товары (RETAIL)' },
  { id: 'SERVICES', label: 'Услуги (SERVICES)' },
  { id: 'B2B', label: 'B2B' },
  { id: 'BOOKING', label: 'Бронирование (BOOKING)' },
  { id: 'EDUCATION', label: 'Обучение (EDUCATION)' },
];

export default function PipelineManagerModal({
  pipelines,
  selectedPipelineId,
  onSelectPipeline,
  onPipelinesChanged,
  onClose,
  onError,
  canManage,
}: PipelineManagerModalProps) {
  const [newPipelineName, setNewPipelineName] = useState('');
  const [newPipelineType, setNewPipelineType] = useState('RETAIL');
  const [newStageName, setNewStageName] = useState('');
  const [newStageColor, setNewStageColor] = useState('#3b82f6');
  const [busy, setBusy] = useState(false);

  const handleCreatePipeline = async (event: React.FormEvent) => {
    event.preventDefault();
    if (!newPipelineName.trim()) return;
    setBusy(true);
    try {
      const created: Pipeline = await fetchApi('/pipelines', {
        method: 'POST',
        body: JSON.stringify({ name: newPipelineName.trim(), businessType: newPipelineType }),
      });
      setNewPipelineName('');
      onPipelinesChanged();
      if (created?.id) onSelectPipeline(created.id);
    } catch (error: any) {
      onError(error?.message || 'Не удалось создать воронку');
    } finally {
      setBusy(false);
    }
  };

  const handleAddStage = async (pipelineId: number) => {
    if (!newStageName.trim()) return;
    setBusy(true);
    try {
      await fetchApi(`/pipelines/${pipelineId}/stages`, {
        method: 'POST',
        body: JSON.stringify({ name: newStageName.trim(), color: newStageColor }),
      });
      setNewStageName('');
      onPipelinesChanged();
    } catch (error: any) {
      onError(error?.message || 'Не удалось добавить этап');
    } finally {
      setBusy(false);
    }
  };

  const handleDeleteStage = async (stage: PipelineStageItem) => {
    if (!window.confirm(`Удалить этап «${stage.name}»?`)) return;
    setBusy(true);
    try {
      await fetchApi(`/pipelines/stages/${stage.id}`, { method: 'DELETE' });
      onPipelinesChanged();
    } catch (error: any) {
      onError(error?.message || 'Не удалось удалить этап');
    } finally {
      setBusy(false);
    }
  };

  const handleRenameStage = async (stageId: number, patch: { name?: string; color?: string }) => {
    setBusy(true);
    try {
      await fetchApi(`/pipelines/stages/${stageId}`, { method: 'PUT', body: JSON.stringify(patch) });
      onPipelinesChanged();
      return true;
    } catch (error: any) {
      onError(error?.message || 'Не удалось изменить этап');
      return false;
    } finally {
      setBusy(false);
    }
  };

  const handleNewPipelineNameChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setNewPipelineName(event.target.value);
  };
  const handleNewPipelineTypeChange = (event: React.ChangeEvent<HTMLSelectElement>) => {
    setNewPipelineType(event.target.value);
  };
  const handleNewStageNameChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setNewStageName(event.target.value);
  };
  const handleNewStageColorChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setNewStageColor(event.target.value);
  };
  const stopPropagation = (event: React.MouseEvent) => event.stopPropagation();

  return (
    <div className="modal-backdrop" onClick={onClose}>
      <div className="deal-modal pipeline-modal" onClick={stopPropagation} role="dialog" aria-modal="true">
        <div className="deal-modal-header">
          <h2>Управление воронками и этапами продаж</h2>
          <button type="button" className="modal-close" onClick={onClose} aria-label="Закрыть">×</button>
        </div>

        <div className="deal-modal-body pipeline-modal-body">
          {!canManage && (
            <div className="pipeline-readonly-note">
              Настраивать воронки могут владелец и администратор. Вы можете выбрать активную воронку для своей доски.
            </div>
          )}

          {canManage && (
            <div className="pipeline-create-section">
              <h4>+ Создать новую воронку</h4>
              <form onSubmit={handleCreatePipeline} className="pipeline-create-form">
                <input
                  type="text"
                  placeholder="Название воронки (например: Продажи курсов)"
                  value={newPipelineName}
                  onChange={handleNewPipelineNameChange}
                  required
                />
                <select value={newPipelineType} onChange={handleNewPipelineTypeChange} aria-label="Тип бизнеса">
                  {BUSINESS_TYPES.map((type) => (
                    <option key={type.id} value={type.id}>{type.label}</option>
                  ))}
                </select>
                <button type="submit" className="btn-primary-deal" disabled={busy}>Создать</button>
              </form>
            </div>
          )}

          <div>
            <h4 className="pipeline-list-title">Ваши воронки и их этапы:</h4>
            <div className="pipeline-list">
              {pipelines.map((pipeline) => (
                <PipelineCard
                  key={pipeline.id}
                  pipeline={pipeline}
                  isSelected={selectedPipelineId === pipeline.id}
                  canManage={canManage}
                  busy={busy}
                  newStageName={newStageName}
                  newStageColor={newStageColor}
                  onSelect={onSelectPipeline}
                  onAddStage={handleAddStage}
                  onDeleteStage={handleDeleteStage}
                  onRenameStage={handleRenameStage}
                  onNewStageNameChange={handleNewStageNameChange}
                  onNewStageColorChange={handleNewStageColorChange}
                />
              ))}
            </div>
          </div>
        </div>

        <div className="deal-modal-footer">
          <button type="button" className="btn-save" onClick={onClose}>Готово</button>
        </div>
      </div>
    </div>
  );
}

interface PipelineCardProps {
  pipeline: Pipeline;
  isSelected: boolean;
  canManage: boolean;
  busy: boolean;
  newStageName: string;
  newStageColor: string;
  onSelect: (pipelineId: number) => void;
  onAddStage: (pipelineId: number) => void;
  onDeleteStage: (stage: PipelineStageItem) => void;
  onRenameStage: (stageId: number, patch: { name?: string; color?: string }) => Promise<boolean>;
  onNewStageNameChange: (event: React.ChangeEvent<HTMLInputElement>) => void;
  onNewStageColorChange: (event: React.ChangeEvent<HTMLInputElement>) => void;
}

function PipelineCard({
  pipeline,
  isSelected,
  canManage,
  busy,
  newStageName,
  newStageColor,
  onSelect,
  onAddStage,
  onDeleteStage,
  onRenameStage,
  onNewStageNameChange,
  onNewStageColorChange,
}: PipelineCardProps) {
  const handleSelect = () => onSelect(pipeline.id);
  const handleAddStage = () => onAddStage(pipeline.id);

  return (
    <div className={`pipeline-card${isSelected ? ' active' : ''}`}>
      <div className="pipeline-card-header">
        <div className="pipeline-card-info">
          <b>{pipeline.name}</b>
          {pipeline.isDefault && <span className="pill green">Основная</span>}
          <span className="pill gray">{pipeline.businessType}</span>
        </div>
        <button type="button" className="btn-secondary-deal" onClick={handleSelect}>
          {isSelected ? 'Выбрана' : 'Сделать активной'}
        </button>
      </div>

      <div className="pipeline-stages-list">
        {pipeline.stages.map((stage) => (
          <StageChip
            key={stage.id}
            stage={stage}
            canManage={canManage}
            busy={busy}
            onDelete={onDeleteStage}
            onRename={onRenameStage}
          />
        ))}
      </div>

      {canManage && (
        <div className="pipeline-add-stage">
          <input
            type="text"
            placeholder="+ Новый этап воронки..."
            value={newStageName}
            onChange={onNewStageNameChange}
            aria-label="Название нового этапа"
          />
          <input type="color" value={newStageColor} onChange={onNewStageColorChange} aria-label="Цвет нового этапа" />
          <button type="button" onClick={handleAddStage} disabled={busy}>Добавить этап</button>
        </div>
      )}
    </div>
  );
}

interface StageChipProps {
  stage: PipelineStageItem;
  canManage: boolean;
  busy: boolean;
  onDelete: (stage: PipelineStageItem) => void;
  onRename: (stageId: number, patch: { name?: string; color?: string }) => Promise<boolean>;
}

/**
 * Inline rename and recolour.
 *
 * `PipelineService.updateStage` has existed since the first version and nothing
 * ever called it, so a typo in a stage name could not be corrected — the only
 * remedy was deleting the stage, which took its deals' placement with it.
 */
function StageChip({ stage, canManage, busy, onDelete, onRename }: StageChipProps) {
  const [editing, setEditing] = useState(false);
  const [draftName, setDraftName] = useState(stage.name);
  const [draftColor, setDraftColor] = useState(stage.color || '#3b82f6');

  const startEditing = () => {
    setDraftName(stage.name);
    setDraftColor(stage.color || '#3b82f6');
    setEditing(true);
  };

  const cancelEditing = () => setEditing(false);

  const commit = async () => {
    const name = draftName.trim();
    if (!name) return;
    const patch: { name?: string; color?: string } = {};
    if (name !== stage.name) patch.name = name;
    if (draftColor !== (stage.color || '#3b82f6')) patch.color = draftColor;
    if (Object.keys(patch).length === 0) {
      setEditing(false);
      return;
    }
    const ok = await onRename(stage.id, patch);
    if (ok) setEditing(false);
  };

  const handleKeyDown = (event: React.KeyboardEvent<HTMLInputElement>) => {
    if (event.key === 'Enter') {
      event.preventDefault();
      void commit();
    } else if (event.key === 'Escape') {
      event.preventDefault();
      cancelEditing();
    }
  };

  const handleNameChange = (event: React.ChangeEvent<HTMLInputElement>) => setDraftName(event.target.value);
  const handleColorChange = (event: React.ChangeEvent<HTMLInputElement>) => setDraftColor(event.target.value);
  const handleCommit = () => void commit();
  const handleDelete = () => onDelete(stage);

  if (editing) {
    return (
      <div className="pipeline-stage-chip editing" style={{ borderLeft: `3px solid ${draftColor}` }}>
        <input
          type="color"
          value={draftColor}
          onChange={handleColorChange}
          className="stage-color-input"
          aria-label={`Цвет этапа ${stage.name}`}
        />
        <input
          type="text"
          value={draftName}
          onChange={handleNameChange}
          onKeyDown={handleKeyDown}
          className="stage-rename-input"
          aria-label={`Название этапа ${stage.name}`}
          autoFocus
        />
        <button type="button" className="stage-icon-btn confirm" onClick={handleCommit} disabled={busy} aria-label="Сохранить название этапа">
          <Check size={13} />
        </button>
        <button type="button" className="stage-icon-btn" onClick={cancelEditing} aria-label="Отменить переименование">
          <X size={13} />
        </button>
      </div>
    );
  }

  return (
    <div className="pipeline-stage-chip" style={{ borderLeft: `3px solid ${stage.color || '#3b82f6'}` }}>
      <span>{stage.name}</span>
      {stage.type === 'won' && <span className="stage-type-hint won">успех</span>}
      {stage.type === 'lost' && <span className="stage-type-hint lost">отказ</span>}
      {canManage && (
        <>
          <button type="button" className="stage-icon-btn" onClick={startEditing} aria-label={`Переименовать этап ${stage.name}`}>
            <Pencil size={12} />
          </button>
          <button type="button" className="stage-delete-btn" onClick={handleDelete} aria-label={`Удалить этап ${stage.name}`}>
            <Trash2 size={12} />
          </button>
        </>
      )}
    </div>
  );
}
